import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/use-auth'

export function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [tempToken, setTempToken] = useState<string | null>(null)
  const { login, loginError, isLoggingIn, verifyMfa, verifyMfaError, isVerifyingMfa } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    const reason = sessionStorage.getItem('logout_reason')
    if (reason === 'idle') {
      toast.warning('Sesión cerrada por inactividad. Vuelve a iniciar sesión.')
      sessionStorage.removeItem('logout_reason')
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const result = await login({ email, password })
      if ('mfaRequired' in result) {
        setTempToken(result.tempToken)
        return
      }
      if ('mfaSetupRequired' in result) {
        sessionStorage.setItem('roi365_mfa_setup_token', result.setupToken)
        navigate({ to: '/login/mfa-setup' })
        return
      }
      navigate({ to: '/' })
    } catch {
      // Error handled by loginError state
    }
  }

  const handleVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!tempToken) return
    try {
      await verifyMfa({ tempToken, code: mfaCode.replace(/\s+/g, '') })
      navigate({ to: '/' })
    } catch {
      // Error handled by verifyMfaError state
    }
  }

  const cancelMfa = () => {
    setTempToken(null)
    setMfaCode('')
    setPassword('')
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      <div
        className="w-full max-w-md rounded-xl border p-8"
        style={{
          backgroundColor: 'var(--color-surface)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <img
            src="/logo-ROI365.svg"
            alt="ROI365"
            className="w-full max-w-sm object-contain"
          />
          <p className="mt-10 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Microsoft 365 License Optimization
          </p>
        </div>

        {/* Form */}
        {!tempToken ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {loginError && (
              <div
                className="rounded-md px-4 py-3 text-sm"
                style={{
                  backgroundColor: 'var(--color-error-light)',
                  color: 'var(--color-error-dark)',
                }}
              >
                {loginError.message}
              </div>
            )}

            <div>
              <label
                htmlFor="email"
                className="mb-1.5 block text-sm font-medium"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="focus-ring w-full rounded-md border px-3 py-2.5 text-sm transition-colors"
                style={{
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'var(--color-surface)',
                }}
                placeholder="usuario@empresa.com"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-sm font-medium"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Contrasena
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="focus-ring w-full rounded-md border px-3 py-2.5 text-sm transition-colors"
                style={{
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'var(--color-surface)',
                }}
                placeholder="********"
              />
            </div>

            <button
              type="submit"
              disabled={isLoggingIn}
              className="focus-ring w-full rounded-md px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-brand-secondary)' }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = 'var(--color-brand-primary)')
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = 'var(--color-brand-secondary)')
              }
            >
              {isLoggingIn ? 'Iniciando sesion...' : 'Iniciar sesion'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyMfa} className="space-y-4">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Introduce el código de 6 dígitos de tu app autenticadora.
            </p>

            {verifyMfaError && (
              <div
                className="rounded-md px-4 py-3 text-sm"
                style={{
                  backgroundColor: 'var(--color-error-light)',
                  color: 'var(--color-error-dark)',
                }}
              >
                {verifyMfaError.message}
              </div>
            )}

            <div>
              <label
                htmlFor="mfaCode"
                className="mb-1.5 block text-sm font-medium"
                style={{ color: 'var(--color-text-primary)' }}
              >
                Código MFA
              </label>
              <input
                id="mfaCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                className="focus-ring w-full rounded-md border px-3 py-2.5 text-center text-lg tabular-nums tracking-widest transition-colors"
                style={{
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'var(--color-surface)',
                }}
                placeholder="000000"
              />
            </div>

            <button
              type="submit"
              disabled={isVerifyingMfa || mfaCode.length !== 6}
              className="focus-ring w-full rounded-md px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-brand-secondary)' }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = 'var(--color-brand-primary)')
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = 'var(--color-brand-secondary)')
              }
            >
              {isVerifyingMfa ? 'Verificando...' : 'Verificar'}
            </button>

            <button
              type="button"
              onClick={cancelMfa}
              className="w-full rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Cancelar y volver al inicio de sesión
            </button>
          </form>
        )}

        {/* Disclaimer */}
        <div
          className="mt-6 text-center text-xs leading-relaxed"
          style={{ color: 'var(--color-text-tertiary)' }}
        >
          <p className="font-semibold mb-1">Términos y Condiciones de Uso</p>
          <p>
            El acceso y uso de ROI365 está reservado a usuarios autorizados y supone la
            aceptación de sus condiciones de uso. Consulta nuestra{' '}
            <a
              href="https://www.cic.es/Politica-de-Privacidad.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-80"
              style={{ color: 'var(--color-brand-secondary)' }}
            >
              Política de Privacidad
            </a>
            .
          </p>
          <p className="mt-2">
            © 2026 CIC Consulting Informático. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  )
}
